<script setup lang="ts">
import { computed, ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import { PermissionDeniedError, MissingSessionError } from "@/errors";
import { useRoute, useRouter } from "vue-router";
import { useCurrentUser } from "vuefire";
import useVuelidate from "@vuelidate/core";
import { required, email as emailValidator } from "@vuelidate/validators";

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const user = useCurrentUser();

const isAuthenticated = ref<boolean>(false);
const email = ref<string>("");
const errorMessage = ref<string>("");
const isPermissionDeniedError = ref<boolean>(false);
const isMissingSessionError = ref<boolean>(false);
const redirectUrl = typeof route.query.redirect === "string" ? route.query.redirect : null;
const authCorrelationId = typeof route.query.correlationId === "string" ? route.query.correlationId : null;

const authenticateUser = () => {
  errorMessage.value = "";
  isPermissionDeniedError.value = false;
  isMissingSessionError.value = false;

  authStore
    .signInWithEmailResult()
    .then(() => {
      isAuthenticated.value = true;
      router.replace({ path: route.path });
    })
    .catch((error: Error) => {
      errorMessage.value =
        error.message ??
        "Something went wrong on our end. Please try again later.";

      if (error instanceof PermissionDeniedError) {
        isPermissionDeniedError.value = true;
      } else if (error instanceof MissingSessionError) {
        isMissingSessionError.value = true;
      }
    });
};

if (user.value) {
  // If user is already signed in, inform them and don't redirect or attempt sign in.
  // This prevents an error if they click an already-used link.
  isAuthenticated.value = true;
  router.replace({ path: route.path });
} else if (route.query.mode === "signIn") {
  // User is not logged in, and URL is valid. Attempt sign-in.
  authenticateUser();
} else {
  // 'mode=signIn' is a param from Google OAuth2 callback,
  // if it doesn't have that then it isn't an auth callback
  // So redirect to the sign in page
  router.push({ name: "signin" });
}

const handleTryAgainButton = (event: Event) => {
  event.preventDefault();
  authStore.clearEmailForSignin();
  router.push({
    name: "signin",
    query: { redirect: (route.query.redirect as string) || "/" },
  });
};

// This flow occurs when a user opens the magic link in a different browser than the one
// where it was originally requested. In this case, the email required by Firebase is not
// available in local storage, so the user must be prompted to enter it.
const handleContinueButton = (event: Event) => {
  event.preventDefault();
  authStore.setEmailForSignin(email.value);
  authenticateUser();
};

const handleProceedToContentButton = () => {
  if (!redirectUrl) {
    return;
  }

  if (!authCorrelationId) {
    router.push(redirectUrl);
    return;
  }

  const separator = redirectUrl.includes('?') ? '&' : '?';
  router.push(`${redirectUrl}${separator}correlationId=${authCorrelationId}`);
};

const shouldShowProceedToContent = computed(() => {
  return redirectUrl && redirectUrl !== '/'
});

const rules = {
  email: { required, email: emailValidator },
};
const v$ = useVuelidate(rules, { email });
</script>

<template>
  <div class="card mx-auto">
    <div class="card-body">
      <div v-if="isAuthenticated && shouldShowProceedToContent" id="auth-message">
        <h1 class="card-title">
          You have been authenticated.
        </h1>
        <p class="card-text">
          You can now close this tab or:
        </p>
        <button 
          id="proceed-to-content-button"
          class="secondary d-block w-100"
          @click="handleProceedToContentButton"
        >
          Proceed to content
        </button>
      </div>
      <div v-else-if="isAuthenticated" id="auth-message">
        <h1 class="card-title">
          You have been authenticated.
        </h1>
        <p class="card-text">
          You can now close this tab.
        </p>
      </div>
      <div v-else-if="isMissingSessionError" id="email-prompt">
        <h1 class="card-title">
          Confirm your email
        </h1>
        <div class="form-group mb-4">
          <input
            id="email-input"
            v-model="v$.email.$model"
            type="email"
            placeholder="Enter your email"
            class="form-control"
          >
        </div>
        <div class="d-grid">
          <button
            id="continue-button"
            :disabled="v$.email.$invalid"
            @keydown.enter="handleContinueButton"
            @click="handleContinueButton"
          >
            Continue
          </button>
        </div>
      </div>
      <div v-else-if="errorMessage != ''" class="error-message">
        {{ errorMessage }}
        <div v-if="!isPermissionDeniedError" class="m-4">
          <button @click="handleTryAgainButton">
            Try again
          </button>
        </div>
      </div>
      <div v-else>
        <p class="loading">
          Please wait...
        </p>
      </div>
    </div>
  </div>
</template>
